import { Helmet } from "react-helmet-async";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://www.lawinetech.com";
const DEFAULT_OG = `${SITE_URL}/og-default.jpg`;

/**
 * Seo — drop-in SEO/OG/Twitter/Schema.org wrapper.
 *
 * Props:
 *  - title        (string)   — short title; appended with "| La WineTech"
 *  - description  (string)   — meta description (max ~160 chars)
 *  - path         (string)   — page path (e.g. "/members/vineatech")
 *  - image        (string?)  — absolute or relative URL for OG/Twitter (1200x630 recommended)
 *  - type         (string?)  — og:type ("website" | "article" | "profile" | "event")
 *  - jsonLd       (object?)  — Schema.org JSON-LD payload
 *  - noindex      (bool?)    — set true for portal/admin pages
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | La WineTech` : "La WineTech | The Global Wine Innovation Ecosystem";
  const desc = description || "La WineTech connects startups, producers, technology providers, researchers, investors and ecosystem partners driving innovation across the global wine industry.";
  const url = `${SITE_URL}${path}`;
  let ogImage = image || DEFAULT_OG;
  if (ogImage && !/^https?:\/\//i.test(ogImage)) {
    ogImage = ogImage.startsWith("/") ? `${SITE_URL}${ogImage}` : `${SITE_URL}/${ogImage}`;
  }
  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="La WineTech" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@LaWineTech" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
